import { useContext, useEffect, useState } from 'react';

import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Flex, Space, Spin, Tooltip, message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

import { SubscriptionContext } from '@/App';
import {
	useDeleteSingleMonitor,
	useGetSingleMonitorList,
} from '@/api/monitor_collections';
import ViewContentMonitorVideoDetailsDrawer from '@/components/drawers/ViewContentMonitorVideoDetailsDrawer';
import { SUBSCRIPTIONS_TYPES_ENUM } from '@/const';
import { useDebounce } from '@/hooks';
import hasSubscriptionType from '@/lib/helpers/hasSubscriptionType';
import NotFound from '@/pages/404';
import { PageContext } from '@/providers/PageContextProvider';

import { IPagination } from '../../video-intelligence-on-demand';
import SingleMonitorListHistoryTable from './SingleMonitorListHistoryTable';
import { singleMonitorListColumns } from './data';

export default function SingleContentMonitorList() {
	const navigate = useNavigate();
	const { id = '', list_id } = useParams<{ id: string; list_id: string }>();
	const { subscriptions, subscriptionLoading } =
		useContext(SubscriptionContext);
	const { setPageTitle, openDrawer, setIsCopilotCreditsCounterVisible } =
		useContext(PageContext);
	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});

	const [search, setSearch] = useState('');
	const [sortBy, setSortBy] = useState<'data_processed'>('data_processed');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
	const debouncedSearch = useDebounce(search);

	const { mutate: deleteMonitor, isLoading: isDeleteMonitorLoading } =
		useDeleteSingleMonitor();

	const {
		data: singleMonitorListData,
		isLoading,
		isFetching,
		refetch,
	} = useGetSingleMonitorList({
		id: id ? Number(id) : null,
		list_id: list_id ? Number(list_id) : null,
		enabled: id ? !!Number(id) : false,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
		page: pagination.page,
		limit: pagination.limit,
		sortBy,
		sort: sortOrder,
	});

	const total = singleMonitorListData?.data?.data?.pagination?.total ?? 1;
	const monitorItem = singleMonitorListData?.data?.data?.item;
	const listHistoryItems = singleMonitorListData?.data?.data?.items;

	useEffect(() => {
		setPageTitle('Brand Alert');
		setIsCopilotCreditsCounterVisible(true);

		return () => {
			setPageTitle('');
			setIsCopilotCreditsCounterVisible(false);
		};
	}, []);

	useEffect(() => {
		refetch();
	}, [sortOrder, pagination]);

	useEffect(() => {
		if (!subscriptionLoading && subscriptions && subscriptions?.length > 0) {
			if (
				!hasSubscriptionType(subscriptions, SUBSCRIPTIONS_TYPES_ENUM.copilot)
			) {
				navigate('/app/dashboard/product-main-menu');
			}
		} else if (
			!subscriptionLoading &&
			subscriptions &&
			subscriptions?.length === 0
		) {
			navigate('/app/dashboard/product-main-menu');
		}
	}, [subscriptions, subscriptionLoading]);

	const onTableChange = (__: any, _: any, sorter: any) => {
		const { field = 'created_at', order = 'ascend' } = sorter;

		if (field) {
			setSortBy(field);
		}

		if (order) {
			setSortOrder(order === 'ascend' ? 'asc' : 'desc');
		}
	};

	const onDeleteClick = (id: number) => {
		deleteMonitor(id, {
			onSuccess: () => {
				message.success('Removed');
				navigate('/app/dashboard/content-monitor');
				// setSearch("");
			},
			onError: (error) => {
				const err = error as any;
				console.error('Error deleting collection:', err.response.data.message);
				message.error(err.response.data.message);
			},
		});
	};

	if (!isLoading && !isFetching && !monitorItem) {
		return <NotFound />;
	}

	return (
		<Spin spinning={isLoading || isFetching}>
			<div>
				<Flex
					align="center"
					justify="space-between"
					style={{ marginBottom: '1.5rem' }}
				>
					<Flex vertical>
						<h2 style={{ marginBottom: 0 }}>Monitor: {monitorItem?.title}</h2>
						<h3 style={{ marginBottom: 0, textAlign: 'left' }}>
							Category: {monitorItem?.name}
						</h3>
					</Flex>
					<Space>
						<Tooltip title="Refresh" placement="left">
							<Button onClick={() => refetch()} icon={<ReloadOutlined />} />
						</Tooltip>
						<Tooltip title="Back to monitor" placement="left">
							<Button
								onClick={() =>
									navigate(`/app/dashboard/content-monitor/${monitorItem?.id}`)
								}
								icon={<ArrowLeftOutlined />}
							/>
						</Tooltip>
					</Space>
				</Flex>

				<Flex vertical gap={16} style={{ width: '100%' }}>
					{/* <Flex
						gap={8}
						justify="flex-end"
						align="center"
						style={{ width: '100%' }}
					>
						<Input
							placeholder="Search categories"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							style={{ width: '300px' }}
						/>
						<Flex gap={16}>
							<Tooltip title="Delete category" placement="topLeft">
								<Popconfirm
									title="Sure to remove category?"
									onConfirm={() => onDeleteClick(Number(id))}
								>
									<Button
										danger
										disabled={isDeleteMonitorLoading}
										loading={isDeleteMonitorLoading}
									>
										<DeleteOutlined />
									</Button>
								</Popconfirm>
							</Tooltip>
						</Flex>
					</Flex> */}
					<SingleMonitorListHistoryTable
						data={listHistoryItems || []}
						columns={singleMonitorListColumns}
						onTableChange={onTableChange}
						isLoading={isLoading}
						isFetching={isFetching}
						pagination={{ ...pagination, total }}
						setPagination={setPagination}
						onEditCreatorClick={() => {}}
						monitor_id={monitorItem?.id}
					/>
				</Flex>
			</div>
			<ViewContentMonitorVideoDetailsDrawer />
		</Spin>
	);
}
